import { NavLink } from 'react-router-dom';
import { Calendar, MapPin, Mountain, Info } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';

interface User {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
  avatar_url: string | null;
}

interface RideCardProps {
  id: string;
  title: string;
  date: string;
  location: string;
  difficulty: 'Easy' | 'Moderate' | 'Hard' | 'Expert';
  distance: string;
  organizer: User | string;
  multi_day?: boolean;
  end_date?: string | null;
}

const getDifficultyColor = (difficulty: string) => {
  switch (difficulty) {
    case 'Easy':
      return 'bg-green-500';
    case 'Moderate':
      return 'bg-blue-500';
    case 'Hard':
      return 'bg-adv-orange';
    case 'Expert':
      return 'bg-red-500';
    default:
      return 'bg-gray-500';
  }
};

const RideCard = ({
  id,
  title,
  date,
  location,
  difficulty,
  distance,
  organizer,
  multi_day,
  end_date,
}: RideCardProps) => {
  // Handle both User object and string formats for organizer
  const organizerName = typeof organizer === 'string' 
    ? organizer 
    : `${organizer.first_name} ${organizer.last_name}`;

  return (
    <NavLink
      to={`/ride-hub/${id}`}
      className="group block bg-white/90 rounded-lg overflow-hidden transition-all hover:shadow-lg hover:translate-y-[-5px] border border-adv-darkGray/10"
    >
      <div className="p-5">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <h3 className="font-bold text-adv-dark transition-colors group-hover:text-adv-orange text-lg">
              {title}
            </h3>
            <p className="text-adv-darkGray text-sm">
              Organized by {organizerName}
            </p>
          </div>
          <div className={`text-xs font-medium px-2.5 py-1 rounded-full text-white ${getDifficultyColor(difficulty)}`}>
            {difficulty}
          </div>
        </div>
        
        <div className="flex flex-col space-y-2 mb-4">
          <div className="flex items-center text-sm text-adv-darkGray">
            <Calendar size={16} className="mr-2 text-adv-orange" />
            {multi_day && end_date ? `${date} - ${end_date}` : date}
            {multi_day && !end_date && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info size={14} className="ml-2 text-adv-orange cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>This is a multi-day ride</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <div className="flex items-center text-sm text-adv-darkGray">
            <MapPin size={16} className="mr-2 text-adv-orange" /> {location}
          </div>
          <div className="flex items-center text-sm text-adv-darkGray">
            <Mountain size={16} className="mr-2 text-adv-orange" /> {distance}
          </div>
        </div>
        
        <div className="flex items-center text-adv-orange font-medium text-sm">
          View Details
          <svg className="ml-1 w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </NavLink>
  );
};

export default RideCard;
